import clientSide from "@/helpers/clientSide";

/**
 * Parses metadata HTML with custom <purple> tags and sanitizes input.
 * Prevents XSS attacks while allowing business team to use safe formatting tags.
 *
 * Allowed tags: purple, br, strong, em, b, i, u, span, a
 * Allowed attributes: class (span only), href (a only)
 *
 * Example input:  '<purple>Trusted by experts.</purple><br/>Chosen by millions.'
 * Example output: '<span class="text-purple-500">Trusted by experts.</span><br/>Chosen by millions.'
 */
// Module-level Sets for O(1) lookup performance in recursive sanitization
const ALLOWED_TAGS_SET = new Set([
  "purple",
  "purpleSecondary",
  "br",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "span",
  "a",
]);
const ALLOWED_ATTRS_MAP: Record<string, Set<string>> = {
  strong: new Set(["class"]),
  span: new Set(["class"]),
  a: new Set(["href"]),
};

export function parseMetadata(html: string): string {
  if (!html) return "";

  // Step 1: Transform Monarch color tags to styled spans.
  // Some CMS payloads return <purple>text<purple> instead of a proper closing tag.
  const transformed = replaceMonarchColorTag(html, "purple", "text-purple-500");

  const transformedSecondary = replaceMonarchColorTag(
    transformed,
    "purpleSecondary",
    "text-purple-700"
  );

  // DOMParser is not available on the server; return transformed string without DOM sanitization during SSR
  if (!clientSide) {
    return transformedSecondary;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(transformedSecondary, "text/html");

  sanitizeNode(doc.body, ALLOWED_TAGS_SET, ALLOWED_ATTRS_MAP);

  return doc.body.innerHTML;
}

function replaceMonarchColorTag(
  value: string,
  tagName: "purple" | "purpleSecondary",
  className: string
): string {
  const colorTagPattern = new RegExp(
    `<${tagName}>([\\s\\S]*?)<(?:\\/${tagName}|${tagName})>`,
    "gi"
  );

  return value.replace(colorTagPattern, `<span class="${className}">$1</span>`);
}

/**
 * Recursively sanitizes a DOM node by removing disallowed tags and attributes.
 * Disallowed tags are replaced with their children.
 */
function sanitizeNode(
  node: Node,
  allowedTags: Set<string>,
  allowedAttrs: Record<string, Set<string>>
): void {
  // Convert NodeList to Array to safely modify during iteration
  const children = Array.from(node.childNodes);

  children.forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element;
      const tagName = element.tagName.toLowerCase();

      // Remove disallowed tags (replace with their children)
      if (!allowedTags.has(tagName)) {
        const fragment = document.createDocumentFragment();
        while (element.firstChild) {
          fragment.appendChild(element.firstChild);
        }
        element.replaceWith(fragment);
        return;
      }

      // Remove disallowed attributes
      const allowedAttrSet = allowedAttrs[tagName];
      Array.from(element.attributes).forEach((attr) => {
        const attrName = attr.name.toLowerCase();
        const attrValue = attr.value;

        if (!allowedAttrSet?.has(attrName)) {
          element.removeAttribute(attr.name);
          return;
        }

        if (attrName === "href" && !isSafeHref(attrValue)) {
          element.removeAttribute(attr.name);
        }
      });

      // Recursively sanitize child nodes
      sanitizeNode(element, allowedTags, allowedAttrs);
    }
  });
}

function isSafeHref(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return !normalized.startsWith("javascript:");
}
